import React, { type ComponentProps, memo, useCallback, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSharedValue } from 'react-native-reanimated';

import { type FlashListRef } from '@shopify/flash-list';

import { AnimatedViews, Tabs, theme, useResponsive, useVirtualizedScrollspy } from '@sg/garnish';
import { ModifierGroupVariation } from '@sg/graphql-schema';
import {
  checkIfDressingIngredient,
  CustomizationConfirmExitModal,
  CustomizationLoadingOverlay,
  CustomizationReview,
  useCustomizationContext,
  useCustomizationHelpers,
  type useInitCustomizationMachine,
} from '@sg/shared-fe-features-customization';
import { useDietaryRestrictions } from '@sg/shared-fe-features-menu';

import {
  CustomizationBottomBar,
  CustomizationContainer,
  CustomizationCTA,
  CustomizationDetailsCompoundContainer,
  CustomizationDetailsContainer,
  CustomizationFloatingBar,
  CustomizationFloatingControls,
  CustomizationSelectedIngredients,
} from '@order/features-customization';
import { type TelemetryEventName, useTelemetry } from '@order/Telemetry';

import { NotFoundView } from '../../../../../../components';
import { useDietaryPreferencesMachine } from '../../../../../../context';
import { useIsLiquidGlassEnabled } from '../../../../../../hooks';
import { CUSTOMIZATIONS_TABS } from '../../../../constants';
import { type useCustomizationContent } from '../../../../hooks';
import {
  CustomizationHeader,
  CustomizationModals,
  CustomizationNutritionCalculator,
} from '../../components';
import {
  useConfirmExitCustomization,
  useCustomizationNavigation,
  useSortedModifications,
} from '../../hooks';
import {
  CustomizationIngredientsGridPanel,
  CustomizationIngredientsList,
  CustomizationIngredientsNav,
  CustomizationIngredientsReviewPanel,
} from './components';
import { useCustomizationIngredientRows, useCustomizationTabs } from './hooks';
import { type CustomizationIngredientRow, getCustomizationIngredientRowTargetId } from './utils';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationCustomizeView = memo((props: Props) => {
  const {
    actor,
    addressId,
    shouldDisableControls,
    isOutOfStockModalOpen,
    customizationContent,
    closeOutOfStockModal,
  } = props;

  // ─── Customization Context ───────────────────────────────────────────

  const {
    addIngredient,
    removeIngredient,
    removeIngredientCompletely,
    endCustomization,
    getFormattedQuantity,
    checkIfCanAddMoreModifications,
    getMaxPortionsForGroup,
  } = useCustomizationHelpers({ actor });

  const {
    product,
    restaurantSlug,
    totalCost,
    calories,
    modifications,
    activeModifications,
    mixedDressingDetails,
    modificationLimits,
    quantity,
    lineItemId,
    isDressingDetailsEnabled,
  } = useCustomizationContext({ actor });

  // ─── Flags ───────────────────────────────────────────────────────────

  const isEditMode = Boolean(lineItemId);

  const isLiquidGlassEnabled = useIsLiquidGlassEnabled();

  // ─────────────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const {
    isConfirmExitModalVisible,
    cancelExit,
    confirmExit,
    onExitModalShow,
    showConfirmExitModal,
    hasActiveModifications,
  } = useConfirmExitCustomization({ actor, restaurantSlug, addressId });

  const { navigateToPreviousScreen } = useCustomizationNavigation({ restaurantSlug, addressId });

  const { minWidth } = useResponsive();
  const { formatMessage } = useIntl();

  const { restrictions } = useDietaryPreferencesMachine({
    referrer: 'Customization',
  });
  const getRestrictionNotice = useDietaryRestrictions({ restrictions });

  const sortedModificationGroups = useSortedModifications({ actor });

  const ctaModifyCartVariation: CtaVariation = lineItemId ? 'update' : 'add-to-bag';
  const ctaVariation: CtaVariation = isDressingDetailsEnabled ? 'continue' : ctaModifyCartVariation;

  const handleBackButtonPress = hasActiveModifications
    ? showConfirmExitModal
    : navigateToPreviousScreen;

  // ─── Refs ────────────────────────────────────────────────────────────

  const detailsContainerRef = useRef<ScrollView>(null);
  const navScrollViewRef = useRef<ScrollView>(null);
  const listRef = useRef<FlashListRef<CustomizationIngredientRow>>(null);

  // ─── State ───────────────────────────────────────────────────────────

  const [activeIngredientId, setActiveIngredientId] = useState<string>();
  const [requestedScrollGroupId, setRequestedScrollGroupId] = useState<string>();

  const clearActiveIngredientId = useCallback(() => {
    setActiveIngredientId(undefined);
  }, []);

  // ─── Tabs ────────────────────────────────────────────────────────────

  const { tabs, activeTabItemId, setActiveTabItemId } = useCustomizationTabs(); // prettier-ignore

  const isAddIngredientsActive = activeTabItemId === CUSTOMIZATIONS_TABS.ADD_INGREDIENTS; // prettier-ignore
  const isReviewActive = activeTabItemId === CUSTOMIZATIONS_TABS.REVIEW;

  // ─── Scrollspy ───────────────────────────────────────────────────────

  const { rows: ingredientRows, itemsPerRow: ingredientsPerRow } = useCustomizationIngredientRows({
    actor,
    modificationGroups: sortedModificationGroups,
  });

  const {
    actorRef: scrollspyActorRef,
    initialScrollIndex,
    scrollTo,
    onViewableItemsChanged,
  } = useVirtualizedScrollspy({
    rows: ingredientRows,
    getTargetId: getCustomizationIngredientRowTargetId,
    listRef,
    defaultTargetId: sortedModificationGroups?.[0]?.id,
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onAddMoreIngredients = useCallback(
    (groupId: string, groupName: string) => {
      scrollTo(groupId);
      setRequestedScrollGroupId(groupId);
      setActiveTabItemId(CUSTOMIZATIONS_TABS.ADD_INGREDIENTS);
      track('customization.review-tab.add-from-section-tapped', { section: groupName }); // prettier-ignore
    },
    [setActiveTabItemId, scrollTo, track],
  );

  const changeTab = useCallback(
    (tab: string) => {
      const isIngredientsTab = tab === CUSTOMIZATIONS_TABS.ADD_INGREDIENTS;
      const eventName: TelemetryEventName = isIngredientsTab
        ? 'customization.ingredients-tab.pressed'
        : 'customization.review-tab.pressed';

      setRequestedScrollGroupId(undefined);
      setActiveTabItemId(tab);
      track(eventName);
    },
    [setActiveTabItemId, track],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const selectedIngredientsScrollSV = useSharedValue(0);

  const updateIngredientsScrollSV = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      selectedIngredientsScrollSV.value = event.nativeEvent.contentOffset.y;
    },
    [selectedIngredientsScrollSV],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!product || !modifications) {
    return <NotFoundView />;
  }

  // ─── Large Breakpoint ────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <>
        <AnimatedViews.Reveal style={styles.revealContainer}>
          <CustomizationContainer>
            <CustomizationDetailsCompoundContainer.Aside>
              <CustomizationDetailsCompoundContainer.ScrollView
                style={styles.container}
                ref={detailsContainerRef}
                stickyHeaderIndices={[0]}
                onScroll={updateIngredientsScrollSV}
                scrollEventThrottle={16}
                bounces={false}
              >
                <CustomizationHeader
                  style={styles.headerSM}
                  scrollOffsetSV={selectedIngredientsScrollSV}
                  restaurantSlug={restaurantSlug}
                  addressId={addressId}
                  text={product.name}
                  onBackButtonPress={handleBackButtonPress}
                />

                <CustomizationSelectedIngredients.Container wrapperStyle={styles.reviewContainer}>
                  <CustomizationSelectedIngredients.List>
                    {sortedModificationGroups?.map((group, index) => {
                      const isLastSection = index === product.modifierGroups.length - 1;

                      const ingredientsForThisSection = activeModifications.filter(
                        (modification) => modification.modificationGroupId === group.id,
                      );

                      return (
                        <CustomizationSelectedIngredients.IngredientKindSection
                          key={group.id}
                          ingredientKindName={group.displayName}
                          style={isLastSection ? styles.withoutBorder : null}
                          helperText={group.helperText}
                          formattedQuantity={getFormattedQuantity(group.name)}
                        >
                          {ingredientsForThisSection.map((modification) => {
                            const { ingredient } = modification;

                            if (!ingredient) return null;

                            const allergenProperties = ingredient.allergenProperties;
                            const restrictionNotice = getRestrictionNotice({ allergenProperties });

                            const isActive = activeIngredientId === ingredient.id;
                            const isDressing = checkIfDressingIngredient(modification); // prettier-ignore
                            const maxPortionsForGroup = getMaxPortionsForGroup(group.name); // prettier-ignore
                            const shouldDisableStepper = maxPortionsForGroup === 1 || isDressing; // prettier-ignore
                            const isSplitPortion = modification.modificationGroupVariation === ModifierGroupVariation.SplitPortion; // prettier-ignore
                            const isHalfPortion = isSplitPortion && modification.quantity < maxPortionsForGroup; // prettier-ignore

                            return (
                              <CustomizationSelectedIngredients.ListItem
                                key={ingredient.id}
                                isActive={isActive}
                                scrollViewRef={detailsContainerRef as React.RefObject<ScrollView>}
                              >
                                <CustomizationSelectedIngredients.IngredientV2
                                  ingredientId={ingredient.id}
                                  ingredientName={ingredient.name}
                                  ingredientAsset={ingredient.asset}
                                  quantity={modification.quantity}
                                  isActive={isActive}
                                  isDisabled={shouldDisableStepper}
                                  restrictionNotice={restrictionNotice}
                                  isSplitPortion={isSplitPortion}
                                  isHalfPortion={isHalfPortion}
                                  setActive={setActiveIngredientId}
                                  setInactive={clearActiveIngredientId}
                                  onIncreaseQuantity={addIngredient}
                                  onDecreaseQuantity={removeIngredient}
                                  onRemove={removeIngredientCompletely}
                                />
                              </CustomizationSelectedIngredients.ListItem>
                            );
                          })}

                          <CustomizationReview.AddMoreIngredientsButton
                            modificationGroupId={group.id}
                            modificationGroupName={group.displayName}
                            onPress={onAddMoreIngredients}
                            isDisabled={!checkIfCanAddMoreModifications(group.name)}
                          />
                        </CustomizationSelectedIngredients.IngredientKindSection>
                      );
                    })}
                  </CustomizationSelectedIngredients.List>
                </CustomizationSelectedIngredients.Container>
              </CustomizationDetailsCompoundContainer.ScrollView>
            </CustomizationDetailsCompoundContainer.Aside>

            {sortedModificationGroups && sortedModificationGroups?.length > 0 ? (
              <View style={styles.ingredientsGridContainer}>
                <CustomizationIngredientsNav
                  scrollspyActorRef={scrollspyActorRef}
                  modificationGroups={sortedModificationGroups}
                  navScrollViewRef={navScrollViewRef}
                  onPress={scrollTo}
                />

                <CustomizationIngredientsList
                  ref={listRef}
                  rows={ingredientRows}
                  itemsPerRow={ingredientsPerRow}
                  actor={actor}
                  initialScrollIndex={initialScrollIndex}
                  onViewableItemsChanged={onViewableItemsChanged}
                  contentContainerStyle={styles.ingredientsListContent}
                />

                <CustomizationBottomBar withGradient>
                  <CustomizationNutritionCalculator.Floating
                    calories={calories}
                    activeModifications={modifications.active}
                    mixedDressingDetails={mixedDressingDetails}
                    product={product}
                  />

                  <CustomizationCTA
                    variation={ctaVariation}
                    cost={totalCost}
                    quantity={quantity}
                    onPress={endCustomization}
                  />
                </CustomizationBottomBar>
              </View>
            ) : null}

            <CustomizationLoadingOverlay show={shouldDisableControls} />
          </CustomizationContainer>
        </AnimatedViews.Reveal>

        <CustomizationModals
          actor={actor}
          customizationContent={customizationContent}
          isOutOfStockModalOpen={isOutOfStockModalOpen}
          closeOutOfStockModal={closeOutOfStockModal}
          setScrollTargetId={scrollTo}
        />

        <CustomizationConfirmExitModal
          isVisible={isConfirmExitModalVisible}
          size="md"
          message={customizationContent?.orderCustomizeConfirmExitMessage} // prettier-ignore
          confirmButtonTitle={customizationContent?.orderCustomizeConfirmExitConfirmButtonTitle} // prettier-ignore
          cancelButtonTitle={customizationContent?.orderCustomizeConfirmExitCancelButtonTitle} // prettier-ignore
          target={isEditMode ? formatMessage(messages.bag) : formatMessage(messages.menu)} // prettier-ignore
          onConfirm={confirmExit}
          onCancel={cancelExit}
          onShow={onExitModalShow}
        />
      </>
    );
  }

  // ─── Small Breakpoint ────────────────────────────────────────────────

  return (
    <>
      <CustomizationContainer style={styles.customizationContainerXS}>
        <AnimatedViews.Reveal style={styles.revealContainer}>
          <View style={styles.headerXS}>
            <CustomizationHeader
              scrollOffsetSV={selectedIngredientsScrollSV}
              restaurantSlug={restaurantSlug}
              addressId={addressId}
              text={product.name}
              spacing="narrow"
              onBackButtonPress={handleBackButtonPress}
            />

            <Tabs.ButtonBar
              tabs={tabs}
              isFluid={true}
              activeTabId={activeTabItemId}
              onChange={changeTab}
              style={styles.tabButtonsBar}
            />
          </View>

          <CustomizationDetailsContainer
            scrollEventThrottle={16}
            onScroll={updateIngredientsScrollSV}
            bounces={false}
            isFluid={true}
            contentContainerStyle={styles.panelContainer}
          >
            <Tabs.Panel
              isActive={isAddIngredientsActive}
              tabId={CUSTOMIZATIONS_TABS.ADD_INGREDIENTS}
              panelId={CUSTOMIZATIONS_TABS.ADD_INGREDIENTS}
              style={isAddIngredientsActive ? styles.panel : null}
            >
              <CustomizationIngredientsGridPanel
                actor={actor}
                activeTargetId={requestedScrollGroupId}
                modificationGroups={sortedModificationGroups}
              />
            </Tabs.Panel>

            <Tabs.Panel
              isActive={isReviewActive}
              tabId={CUSTOMIZATIONS_TABS.REVIEW}
              panelId={CUSTOMIZATIONS_TABS.REVIEW}
              style={isReviewActive ? styles.panel : null}
            >
              <CustomizationIngredientsReviewPanel
                product={product}
                activeModifications={activeModifications}
                modifierGroups={sortedModificationGroups}
                modificationLimits={modificationLimits}
                addIngredientModification={addIngredient}
                removeIngredientModification={removeIngredient}
                completelyRemoveIngredientModification={removeIngredientCompletely}
                getFormattedQuantity={getFormattedQuantity}
                onAddMoreIngredients={onAddMoreIngredients}
                checkIfCanAddMore={checkIfCanAddMoreModifications}
              />
            </Tabs.Panel>
          </CustomizationDetailsContainer>
        </AnimatedViews.Reveal>

        <CustomizationFloatingBar>
          <CustomizationNutritionCalculator.Floating
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            calories={calories}
            activeModifications={modifications.active}
            mixedDressingDetails={mixedDressingDetails}
            product={product}
            variant="compact"
          />

          <CustomizationFloatingControls
            isLiquidGlassEnabled={isLiquidGlassEnabled}
            shouldDisableControls={shouldDisableControls}
            onBackButtonPress={handleBackButtonPress}
          >
            <CustomizationCTA
              isLiquidGlassEnabled={isLiquidGlassEnabled}
              variation={ctaVariation}
              cost={totalCost}
              quantity={quantity}
              onPress={endCustomization}
            />
          </CustomizationFloatingControls>
        </CustomizationFloatingBar>

        <CustomizationLoadingOverlay show={shouldDisableControls} />
      </CustomizationContainer>

      <CustomizationModals
        actor={actor}
        customizationContent={customizationContent}
        isOutOfStockModalOpen={isOutOfStockModalOpen}
        closeOutOfStockModal={closeOutOfStockModal}
        setScrollTargetId={scrollTo}
      />

      <CustomizationConfirmExitModal
        isVisible={isConfirmExitModalVisible}
        size="md"
        message={customizationContent?.orderCustomizeConfirmExitMessage} // prettier-ignore
        confirmButtonTitle={customizationContent?.orderCustomizeConfirmExitConfirmButtonTitle} // prettier-ignore
        cancelButtonTitle={customizationContent?.orderCustomizeConfirmExitCancelButtonTitle} // prettier-ignore
        target={isEditMode ? formatMessage(messages.bag) : formatMessage(messages.menu)} // prettier-ignore
        onConfirm={confirmExit}
        onCancel={cancelExit}
        onShow={onExitModalShow}
      />
    </>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  menu: {
    defaultMessage: 'menu',
    description: 'Customization | Confirm exit modal | Menu',
  },
  bag: {
    defaultMessage: 'bag',
    description: 'Customization | Confirm exit modal | Bag',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingTop: 0,
  },
  customizationContainerXS: {
    backgroundColor: theme.colors.OATMEAL,
  },
  revealContainer: {
    flex: 1,
  },
  headerXS: {
    backgroundColor: theme.colors.CREAM,
  },
  headerSM: {
    width: '100%',
    flexDirection: 'row',
    paddingVertical: theme.spacing['1'],
    marginBottom: theme.spacing['4'],
  },
  tabButtonsBar: {
    paddingHorizontal: theme.spacing['4'],
  },
  panelContainer: {
    flex: 1,
  },
  panel: {
    flex: 1,
  },
  reviewContainer: {
    marginHorizontal: -theme.spacing['4'],
  },
  ingredientsGridContainer: {
    flex: 1,
  },
  ingredientsListContent: {
    paddingBottom: theme.spacing['30'],
  },
  withoutBorder: {
    borderBottomWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: ReturnType<typeof useInitCustomizationMachine>;
  customizationContent: ReturnType<typeof useCustomizationContent>['fields'];
  addressId: string | undefined;
  isOutOfStockModalOpen: boolean;
  shouldDisableControls: boolean;
  closeOutOfStockModal: () => void;
};

type CtaVariation = ComponentProps<typeof CustomizationCTA>['variation'];
